import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, Play, ShieldCheck, Activity, Brain, HeartPulse } from 'lucide-react';
import ClinicalOutput from '@/components/ClinicalOutput';
import PipelineProgress from '@/components/PipelineProgress';
import { CATALOG_ENGINE_COUNT } from '@/lib/engineCount';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── CANONICAL FOUNDER CHART ────────────────────────────────────────
// Source of truth: /app/memory/FOUNDER_CHART.md (IMMUTABLE).
// Every clinical statement here MUST trace back to a sentence in that file.
// No bereavement, no loneliness, no widower status — the founder's wife is
// alive. Any future edit that introduces clinical content not present in
// FOUNDER_CHART.md is a fabrication and must be rolled back.
const FOUNDER_CHART = {
  age: '73',
  sex: 'Male',
  language: 'English',
  chiefComplaint: 'Multi-morbid health review (founder demo).',
  symptoms:
    '73 year old male. T2DM. Obesity 240 lbs. ' +
    'Prurigo Nodularis (PN) — severe, resistant to IL-17, IL-31, Skyrizi. ' +
    'Plaque psoriasis. Inverse psoriasis. Palmo-plantar psoriasis. ' +
    'C2-T2 laminectomy with lateral mass fusion for OPLL. ' +
    'ADHD. Mother with bipolar disorder.',
  medications:
    'Mounjaro 5mg weekly, Jardiance 25mg daily, Losartan 100mg daily, ' +
    'Metformin 500mg long acting daily, Gabapentin 300mg twice daily, ' +
    'pain meds as needed.',
  existingConditions:
    'T2DM. Obesity. Prurigo Nodularis. Plaque psoriasis. Inverse psoriasis. ' +
    'Palmo-plantar psoriasis. OPLL s/p C2-T2 laminectomy with lateral mass fusion. ' +
    'ADHD. Family history: mother with bipolar disorder.',
  notes:
    '[YC_DEMO] Founder-patient demo run. Patient is the founder of P.AI Global Health. ' +
    'Consent given by virtue of authoring this demo. Not a live patient encounter — ' +
    'excluded from production traction metrics. Source of truth: /app/memory/FOUNDER_CHART.md.',
  communicationMode: 'text',
  viewMode: 'physician',
};

// ─── SYNTHETIC CASE — for Engine 25 demonstration only ──────────────
// Explicitly synthetic. Not a real patient. 3 facts only: age, lives alone,
// reports social isolation. No name, ethnicity, family details, or PHI.
const SYNTHETIC_LONELINESS_CHART = {
  age: '68',
  sex: '',
  language: 'English',
  chiefComplaint: 'Reports social isolation.',
  symptoms: 'Patient X, 68 years old. Lives alone. Reports social isolation.',
  medications: '',
  existingConditions: '',
  notes:
    '[YC_DEMO_SYNTHETIC] Synthetic demo case for Engine 25 (loneliness/social isolation screen). ' +
    'NOT a real patient. 3 stated facts only: age 68, lives alone, reports social isolation. ' +
    'No name, no ethnicity, no personal details.',
  communicationMode: 'text',
  viewMode: 'physician',
};

const PIPELINE_STEPS = [
  { step: 1, name: 'Intake Agent', desc: 'Validating and structuring intake data' },
  { step: 2, name: 'Redactor Agent', desc: 'Sanitizing sensitive patterns' },
  { step: 3, name: 'Normalizer Agent', desc: 'Normalizing medical terminology' },
  { step: 4, name: 'Longitudinal Memory Agent', desc: 'Checking prior encounter history' },
  { step: 5, name: 'Draft Clinical Note Agent', desc: 'Generating SOAP note' },
  { step: 6, name: 'Guardrails Agent', desc: 'Running safety checks' },
  { step: 7, name: 'Audit Log Agent', desc: 'Creating audit trail' },
];

export default function YcDemoPage() {
  const [phase, setPhase] = useState('idle'); // idle | running | done | error
  const [completedSteps, setCompletedSteps] = useState([]);
  const [result, setResult] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [caseMode, setCaseMode] = useState('founder'); // 'founder' | 'synthetic'

  // Auto-scroll the result into view when it lands
  useEffect(() => {
    if (phase === 'done') {
      setTimeout(() => {
        const el = document.getElementById('yc-demo-result');
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  }, [phase]);

  // Hardening — add noindex meta to <head> so search engines do not index this
  // route. Reversible: remove this effect to make the page indexable again.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = 'P.AI — YC Partner Demo (Private)';
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, []);

  const runDemo = useCallback(async (modeOverride) => {
    const mode = modeOverride || caseMode;
    setCaseMode(mode);
    setPhase('running');
    setResult(null);
    setCompletedSteps([]);
    setElapsed(0);

    const t0 = performance.now();
    let stepIdx = 0;
    const interval = setInterval(() => {
      if (stepIdx < PIPELINE_STEPS.length) {
        setCompletedSteps((prev) => [...prev, PIPELINE_STEPS[stepIdx].name]);
        stepIdx += 1;
      }
    }, 300);

    const payload = mode === 'synthetic' ? SYNTHETIC_LONELINESS_CHART : FOUNDER_CHART;

    try {
      const response = await axios.post(`${API}/intake`, payload);
      clearInterval(interval);
      setCompletedSteps(PIPELINE_STEPS.map((s) => s.name));
      setElapsed(((performance.now() - t0) / 1000).toFixed(1));
      setResult(response.data);
      setPhase('done');
    } catch (err) {
      clearInterval(interval);
      setPhase('error');
      toast.error(err.response?.data?.error || 'Demo run failed. Please refresh and try again.');
    }
  }, [caseMode]);

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 text-slate-900"
      data-testid="yc-demo-page"
    >
      {/* Disclaimer banner — visible warning that this is a private demo */}
      <div
        className="w-full bg-amber-50 border-b border-amber-200 px-4 py-2 text-center text-xs sm:text-sm text-amber-800"
        data-testid="yc-demo-disclaimer"
      >
        Live demo on founder&apos;s real chart. Private link for YC partners — please do not share publicly.
      </div>

      {/* Splash header */}
      <div className="max-w-4xl mx-auto px-6 pt-16 pb-8">
        <div className="flex items-center gap-2 mb-6">
          <Badge variant="outline" className="border-blue-200 text-blue-700 bg-blue-50">
            YC Partner Demo · Live Production
          </Badge>
          <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-emerald-50">
            No LLM in the diagnostic loop
          </Badge>
        </div>

        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-tight"
          data-testid="yc-demo-headline"
        >
          I built this on my own chart.
        </h1>
        <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl">
          Seventy-three. T2DM. Two-forty pounds. Severe resistant psoriasis (plaque, inverse, palmo-plantar) + Prurigo Nodularis failed to IL-17, IL-31, Skyrizi.
          Post-OPLL cervical fusion. ADHD. Mother with bipolar disorder. Six medications.
          <br />
          <span className="text-slate-900 font-medium">
            One click runs P.AI on my real chart, live in production.
          </span>
        </p>

        {/* The buttons — two clearly labeled cases */}
        <div className="mt-8 flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => runDemo('founder')}
              disabled={phase === 'running'}
              size="lg"
              className="bg-[#2563EB] hover:bg-blue-700 text-white rounded-md px-8 py-6 text-base font-semibold shadow-md"
              data-testid="yc-demo-run-button"
            >
              {phase === 'running' && caseMode === 'founder' ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Running founder case…
                </>
              ) : (
                <>
                  <Play className="w-5 h-5 mr-2" />
                  Run my real case (founder chart)
                </>
              )}
            </Button>
            <Button
              onClick={() => runDemo('synthetic')}
              disabled={phase === 'running'}
              size="lg"
              variant="outline"
              className="border-amber-300 text-amber-800 hover:bg-amber-50 rounded-md px-8 py-6 text-base font-semibold"
              data-testid="yc-demo-synthetic-button"
            >
              {phase === 'running' && caseMode === 'synthetic' ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Running synthetic case…
                </>
              ) : (
                <>
                  <Play className="w-5 h-5 mr-2" />
                  Synthetic Demo — Engine 25 Loneliness Detection
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-slate-500" data-testid="yc-demo-case-label">
            {caseMode === 'synthetic' ? (
              <span><span className="font-semibold text-amber-700">Synthetic case (NOT a real patient):</span> Patient X, 68 years old, lives alone, reports social isolation. No name, no ethnicity, no personal details. Exists solely to demonstrate Engine 25.</span>
            ) : (
              <span><span className="font-semibold text-slate-700">Founder case:</span> Verbatim multi-morbid chart from <code className="px-1 py-0.5 bg-slate-100 rounded text-[10px]">/app/memory/FOUNDER_CHART.md</code>. No fabricated bereavement, no wife/widow/loneliness content. The founder&apos;s wife is alive.</span>
            )}
          </p>
          {phase === 'done' && elapsed && (
            <span className="text-sm text-slate-500" data-testid="yc-demo-elapsed">
              Completed in {elapsed}s · 7-step deterministic pipeline
            </span>
          )}
        </div>

        {/* Trust strip */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card className="border-slate-200">
            <CardContent className="p-4 flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-emerald-600 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-slate-900">No LLM diagnosis</div>
                <div className="text-xs text-slate-500">Every risk flag traceable to a Python rule.</div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 flex items-start gap-3">
              <Activity className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-slate-900">102 real users</div>
                <div className="text-xs text-slate-500">238 encounters · &lt;$10K all-in · zero paid acquisition.</div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 flex items-start gap-3">
              <Brain className="w-5 h-5 text-purple-600 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-slate-900">{CATALOG_ENGINE_COUNT} deterministic clinical engines</div>
                <div className="text-xs text-slate-500">Including the world's only non-LLM loneliness screen.</div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Pipeline + Result */}
      <div className="max-w-4xl mx-auto px-6 pb-24">
        {phase === 'running' && (
          <div className="mt-2">
            <PipelineProgress steps={PIPELINE_STEPS} completedSteps={completedSteps} />
          </div>
        )}

        {phase === 'done' && result && (
          <div id="yc-demo-result" className="mt-8" data-testid="yc-demo-result">
            <ClinicalOutput result={result} viewMode="physician" />
          </div>
        )}

        {/* Founder signature */}
        <div className="mt-12 pt-8 border-t border-slate-200 flex items-start gap-3">
          <HeartPulse className="w-5 h-5 text-rose-500 mt-1" />
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-slate-900">Sivam Eniasivam</span> — Founder &amp; Patient,
            P.AI Global Health &amp; P.AI Health Mart.
            <br />
            <span className="text-xs text-slate-500">
              This demo runs on my real chart with my consent. Tagged{' '}
              <code className="px-1 py-0.5 rounded bg-slate-100 text-slate-700">source: "yc_demo"</code> in the
              database and excluded from the 102-user production count for honesty.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
